import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Wifi, Waves, Star } from "lucide-react";
import { useEffect, useState } from "react";
import SiteLayout from "@/components/site/SiteLayout";
import BookingWidget from "@/components/site/BookingWidget";
import RoomCard from "@/components/site/RoomCard";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const HERO_IMG = "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Panorama_Photography_of_Saranda.jpg/1920px-Panorama_Photography_of_Saranda.jpg";

// Local-hosted video background (Pexels CC video #29007698, re-encoded for web).
// Two resolutions for bandwidth + JPEG poster while loading.
const HERO_VIDEO_DESKTOP = "/videos/hero-saranda-web.mp4";
const HERO_VIDEO_MOBILE = "/videos/hero-saranda-mobile.mp4";
const HERO_VIDEO_POSTER = "/videos/hero-saranda-poster.jpg";

// Authentic Sarandë gallery — images sourced from Wikipedia / Wikimedia Commons (CC)
const SARANDE_GALLERY = [
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/CIty_of_Saranda_Albania_2016.jpg/1280px-CIty_of_Saranda_Albania_2016.jpg", caption: "View of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Kalaja_e_Lekursit_01.JPG/1280px-Kalaja_e_Lekursit_01.JPG", caption: "Lëkurësi Castle" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Saranda_Albania_Promenade_2016.jpg/1280px-Saranda_Albania_Promenade_2016.jpg", caption: "Promenade of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Ksamill-1.jpg/1280px-Ksamill-1.jpg", caption: "Islets of Ksamil" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/42/Vivari_Channel_within_the_Butrint_National_Park_in_Albania.jpg/1280px-Vivari_Channel_within_the_Butrint_National_Park_in_Albania.jpg", caption: "Butrint National Park" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/Panoramic_view_of_Saranda_from_L%C3%ABkur%C3%ABsi_Castle.jpg/1280px-Panoramic_view_of_Saranda_from_L%C3%ABkur%C3%ABsi_Castle.jpg", caption: "View from Lëkurësi Castle" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/Blue_Eye_Albania_2016_Syri_i_kalter.jpg/1280px-Blue_Eye_Albania_2016_Syri_i_kalter.jpg", caption: "The Blue Eye Spring (Syri i Kaltër)" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Saranda_Albania_2016.jpg/1280px-Saranda_Albania_2016.jpg", caption: "Promenade with the beach" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Cruise_ship_Eurodam_in_Saranda.jpg/1280px-Cruise_ship_Eurodam_in_Saranda.jpg", caption: "Cruise ship Eurodam in Sarandë" },
];

export default function HomePage() {
  const { t, trSetting } = useI18n();
  const [rooms, setRooms] = useState([]);
  const [settings, setSettings] = useState({});
  const [reviews, setReviews] = useState({ reviews: [], average: 0, total: 0 });

  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data.slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/reviews", { params: { featured_only: true, limit: 3 } }).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const heroTitle = trSetting(settings, "hero_title") || t("home.intro_title");
  const seoTitle = trSetting(settings, "seo_title") || "Berdhill Rooms · Boutique stay in Sarandë, Albania";
  const seoDesc = trSetting(settings, "seo_description") ||
    "Boutique rooms and apartments above the Ionian Sea in Sarandë, Albania.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: "Berdhill Rooms",
    description: seoDesc,
    address: {
      "@type": "PostalAddress",
      streetAddress: trSetting(settings, "address") || "Rruga e Butrintit",
      addressLocality: "Sarandë",
      addressCountry: "AL",
      postalCode: "9701",
    },
    telephone: trSetting(settings, "phone"),
    email: trSetting(settings, "email"),
    image: HERO_IMG,
    priceRange: "€€",
    starRating: { "@type": "Rating", ratingValue: "4.5" },
    aggregateRating: reviews.total > 0 ? {
      "@type": "AggregateRating",
      ratingValue: String(reviews.average),
      reviewCount: String(reviews.total),
    } : undefined,
  };

  return (
    <SiteLayout transparentNav noPadding>
      <Seo title={seoTitle} description={seoDesc} image={HERO_IMG} type="website" jsonLd={jsonLd} />

      <section data-testid="home-hero" className="relative min-h-[720px] md:h-[88vh] md:min-h-[640px] w-full overflow-hidden">
        {/* Video background — poster shows immediately, video crossfades in once buffered.
            Two <source>s let the browser pick the smaller mobile file on narrow screens. */}
        <video
          data-testid="home-hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_VIDEO_POSTER}
          aria-hidden="true"
        >
          <source src={HERO_VIDEO_MOBILE} type="video/mp4" media="(max-width: 768px)" />
          <source src={HERO_VIDEO_DESKTOP} type="video/mp4" />
          {/* Final fallback for browsers without <video> support */}
          <img src={HERO_IMG} alt="Sarandë, Albanian Riviera" className="absolute inset-0 w-full h-full object-cover" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-center items-start text-white max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-28 pb-72 md:pb-44">
          <div className="text-[11px] tracking-[0.4em] uppercase mb-6 opacity-90 flex items-center gap-2">
            <MapPin size={14} /> {t("home.location")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[7rem] font-light leading-[1.05] max-w-4xl">
            {heroTitle}
          </h1>
          <p className="mt-6 text-base md:text-lg max-w-xl opacity-90 font-light leading-relaxed">
            {trSetting(settings, "hero_subtitle")}
          </p>
        </div>
        <div className="absolute bottom-4 md:bottom-10 left-0 right-0 z-20 px-3 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <BookingWidget />
          </div>
        </div>
      </section>

      <section className="pt-20 pb-16 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("home.welcome")}</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">{t("home.intro_title")}</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-base md:text-lg leading-relaxed text-brand-text/80">{trSetting(settings, "about_text")}</p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 text-sm">
              <Feature icon={<Waves size={20} />} label={t("home.feat.sea")} />
              <Feature icon={<Wifi size={20} />} label={t("home.feat.wifi")} />
              <Feature icon={<MapPin size={20} />} label={t("home.feat.walk")} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("home.staywithus")}</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3">{t("home.our_rooms")}</h2>
            </div>
            <Link to="/rooms" data-testid="home-view-all-rooms" className="text-sm flex items-center gap-2 text-brand-primary hover:text-brand-primaryHover transition-colors">
              {t("home.view_all")} <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {rooms.map((r) => <RoomCard key={r.id} room={r} />)}
          </div>
        </div>
      </section>

      {reviews.reviews.length > 0 && (
        <section className="py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("home.reviews_kicker")}</div>
                <h2 className="font-display text-4xl md:text-5xl mt-3">{t("home.reviews_title")}</h2>
                {reviews.average > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-brand-muted">
                    <Stars value={reviews.average} /> {reviews.average} / 5 · {reviews.total} reviews
                  </div>
                )}
              </div>
              <Link to="/reviews" className="text-sm flex items-center gap-2 text-brand-primary hover:text-brand-primaryHover">
                {t("nav.reviews")} <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.reviews.map((r) => (
                <article key={r.id} className="bg-white border border-brand-border p-6 rounded-sm">
                  <Stars value={r.rating} />
                  {r.title && <h3 className="font-display text-xl mt-3">{r.title}</h3>}
                  <p className="mt-3 text-sm text-brand-text/80 leading-relaxed">"{r.content}"</p>
                  <div className="mt-5 text-xs text-brand-muted">{r.author_name}{r.country ? ` · ${r.country}` : ""}</div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/5] overflow-hidden rounded-sm">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Saranda_Albania_2016.jpg/1280px-Saranda_Albania_2016.jpg"
              alt="Promenade and beach of Sarandë"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("home.mornings")}</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">{t("home.mornings_title")}</h2>
            <p className="mt-6 text-brand-text/80 leading-relaxed">{t("home.mornings_text")}</p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-brand-primary hover:text-brand-primaryHover" data-testid="home-about-link">
              {t("home.more_about")} <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <ContentBlocksTeaser />

      <SarandeGallery />

      <section className="py-20 lg:py-28 bg-[#231F20] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="text-xs tracking-[0.4em] uppercase opacity-70">{t("home.cta_kicker")}</div>
          <h2 className="font-display text-4xl md:text-5xl mt-4">{t("home.cta_title")}</h2>
          <p className="mt-6 opacity-80 leading-relaxed">{t("home.cta_sub")}</p>
          <Link to="/rooms" data-testid="home-cta-book" className="inline-block mt-10 bg-brand-primary hover:bg-brand-primaryHover text-white px-8 py-4 rounded-sm font-medium tracking-wide">
            {t("home.cta_btn")}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}

function ContentBlocksTeaser() {
  const { lang } = useI18n();
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/content-blocks").then((r) => setBlocks(r.data || [])).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const blocksByKey = Object.fromEntries(blocks.map((b) => [b.section_key, b]));
  const surroundings = blocksByKey.surroundings;
  const faq = blocksByKey.faq;
  const travellers = blocksByKey.travellers_qa;
  const mapUrl = settings?.maps_embed_url?.value;

  function trBlock(b, field) {
    return b?.i18n?.[lang]?.[field] ?? b?.[field] ?? "";
  }
  function trItem(it, field) {
    return it?.i18n?.[lang]?.[field] ?? it?.[field] ?? "";
  }

  if (!surroundings && !faq && !travellers) return null;

  return (
    <>
      {surroundings && (
        <section className="py-20 lg:py-28 bg-white" data-testid="home-surroundings">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{trBlock(surroundings, "subtitle")}</div>
                <h2 className="font-display text-4xl md:text-5xl mt-3">{trBlock(surroundings, "title")}</h2>
              </div>
              <Link to="/info/surroundings" className="text-sm flex items-center gap-2 text-brand-primary hover:text-brand-primaryHover">
                Explore more <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(surroundings.items || []).slice(0, 6).map((it, i) => (
                <article key={i} className="border border-brand-border rounded-md p-5 bg-white hover:border-brand-primary/40 transition-colors">
                  <MapPin size={16} className="text-brand-primary" />
                  <h3 className="font-display text-xl mt-3">{trItem(it, "title")}</h3>
                  <p className="mt-2 text-sm text-brand-muted leading-relaxed">{trItem(it, "content")}</p>
                </article>
              ))}
            </div>
            {mapUrl && (
              <div className="mt-12 rounded-md overflow-hidden border border-brand-border">
                <iframe title="Berdhill Rooms location" src={mapUrl} className="w-full h-72 md:h-96" loading="lazy" />
              </div>
            )}
          </div>
        </section>
      )}

      {(faq || travellers) && (
        <section className="py-20 lg:py-28" data-testid="home-faq">
          <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid md:grid-cols-2 gap-12">
              {[faq, travellers].filter(Boolean).map((b) => (
                <div key={b.section_key}>
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{trBlock(b, "subtitle")}</div>
                  <h2 className="font-display text-3xl md:text-4xl mt-3">{trBlock(b, "title")}</h2>
                  <div className="mt-6 space-y-4">
                    {(b.items || []).slice(0, 4).map((it, i) => (
                      <details key={i} className="border-b border-brand-border pb-4 group">
                        <summary className="cursor-pointer font-medium text-brand-text flex items-start justify-between gap-3 text-sm md:text-base">
                          <span>{trItem(it, "title")}</span>
                          <ChevronRight size={16} className="mt-0.5 text-brand-muted group-open:rotate-90 transition-transform shrink-0" />
                        </summary>
                        <p className="mt-3 text-sm text-brand-muted leading-relaxed">{trItem(it, "content")}</p>
                      </details>
                    ))}
                  </div>
                  <Link to={`/info/${b.section_key}`} className="inline-flex items-center gap-1 mt-6 text-sm text-brand-primary hover:text-brand-primaryHover">
                    See all <ChevronRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function Feature({ icon, label }) {
  return (
    <div>
      <div className="text-brand-primary mb-2">{icon}</div>
      <div className="text-sm">{label}</div>
    </div>
  );
}

export function Stars({ value = 0 }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          className={n <= Math.round(value) ? "fill-brand-primary text-brand-primary" : "text-brand-border"}
        />
      ))}
    </div>
  );
}

function SarandeGallery() {
  return (
    <section data-testid="sarande-gallery" className="py-20 lg:py-28 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-muted">Albanian Riviera · Vlorë County</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3">Discover Sarandë</h2>
          <p className="text-brand-muted mt-4 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Sarandë sits on an open gulf of the Ionian Sea, enjoying a Mediterranean climate
            with over <strong>300 sunny days a year</strong>. In antiquity it was known as
            <em> Onchesmus</em>, a port-town of Chaonia in ancient Epirus. Today it is the
            unofficial capital of the Albanian Riviera, and the gateway to the UNESCO World
            Heritage site of <strong>Butrint</strong>, the <strong>Blue Eye Spring</strong> and the
            beaches of <strong>Ksamil</strong>.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {SARANDE_GALLERY.map((img, i) => (
            <figure
              key={i}
              data-testid={`gallery-img-${i}`}
              className={`group relative overflow-hidden rounded-md ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-[4/5]" : "aspect-square"}`}
            >
              <img
                src={img.src}
                alt={img.caption}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] md:text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                {img.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="text-[11px] text-brand-muted/70 mt-6 text-center">
          Photos & facts: <a href="https://en.wikipedia.org/wiki/Sarand%C3%AB" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-primary">Wikipedia — Sarandë</a> · Wikimedia Commons (CC).
        </p>
      </div>
    </section>
  );
}

